import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import "../../styles/MyProfile.css";
import InternTnC from "./InternTnC";

const Intern = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData] = useFetch();
  const userId = authState.user._id;

  const [internData, setInternData] = useState({
    userId: userId,
    name: authState.user.name,
    gender: 0,
    dob: '',
    email: authState.user.email,
    mobile: '',
    addOnEmail: '',
    addOnContact: '',
    address: '',
    district: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    upiID: '',
    education: '',
    institution: '',
    socialLinks: '',
    personalPageLink: '',
    bio: '',
    remarks: '',
    isActive: 1,
    isDeleted: 0,
    isInternPic: false,
    think5whysCode: authState.user.think5whyscode,
    createdBy: userId,
    dateCreated: new Date(),
    dateUpdated: new Date(),
    dateStarts: null,
    extensionRemarks: '',
    dateExtends: null,
    dateEnds: null,
    internStatus: "Candidate",
    stipend: 0.00,
    certificateText: '',
    mentorName: '',
    mentorEmail: '',
    mentorContact: '',
    mentorCode: '',
    mentorDetails: '',
  });

  const [pictureData, setPictureData] = useState({
    think5whysCode: authState.user.think5whyscode,
    isInternPic: false,
  });

  const [originalInternData, setOriginalInternData] = useState({});
  const [internExists, setInternExists] = useState(false);
  const [internId, setInternId] = useState('');
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState('');
  const [creatingIntern, setCreatingIntern] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserIntern(userId);
  }, [userId]);

  const fetchUserIntern = useCallback(() => {
    try {
      const config = {
        url: "/intern/byid",
        method: 'post',
        data: { userId: userId },
        headers: { Authorization: authState.token },
      };
      fetchData(config)
        .then(response => {
          if (response && response.status) {
            // console.log("intern exists ",response);
            setInternExists(true);
            const { intern } = response;
            setInternId(intern._id);
            setInternData({
              ...internData,
              ...intern,
            });
            setPictureData({
              think5whysCode: intern.think5whyscode,
              isInternPic: intern.isInternPic,
            });
          } else {
            setCreateSuccessMsg("Sorry! Intern Not Found. Please retry.");
          }
        })
        .catch(() => {
          setCreateSuccessMsg("Sorry! Intern Not Found. Please retry.");
        });
    } catch {
      setCreateSuccessMsg("Sorry! Intern not found. Please retry.");
    }
  }, [userId, fetchData, setInternExists]);

  const handleInternUpdate = useCallback(() => {
    validateInputs();
    const config = {
      url: `/intern/updateintern`,
      method: 'post',
      data: { internData: internData, userId: userId},
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setOriginalInternData(internData);
          setUpdateSuccess(true);
          setUpdateSuccessMsg("Intern updated successfully!");
        } else {
          setUpdateSuccessMsg("Error updating intern! Please retry.");
        }
      })
      .catch(() => {
        setUpdateSuccessMsg("Error updating intern! Please retry.");
      });
  }, [internData, internId, fetchData]);

  const handleCancel = () => {
    setInternData(originalInternData);
  };

  const handleCreateIntern = () => {
    setCreatingIntern(true);
    fetchInternCreate();
  };

  const fetchInternCreate = useCallback(() => {
    const config = {
      url: "/intern",
      method: 'post',
      data: internData,
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setCreateSuccess(true);
          setCreateSuccessMsg("Intern Created Successfully");
        } else {
          setCreateSuccessMsg("Sorry! Intern Not Created. Please retry.");
        }
      })
      .catch(() => {
        setCreateSuccessMsg("Sorry! Intern Not Created. Please retry.");
      });
  }, [internData, fetchData]);

  const validateInputs = () => {
    let isValid = true;
    const errors = {};
    const mobileRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // if (!internData.gender) {
    //   errors.gender = 'Gender is required.';
    //   isValid = false;
    // }

    if (internData.gender === undefined || internData.gender === null) {
      errors.gender = 'Gender is required.';
      isValid = false;
    }

    if (!internData.dob) {
      errors.dob = 'Date of Birth is required.';
      isValid = false;
    } else {
      const today = new Date();
      const dob = new Date(internData.dob);
      const age = today.getFullYear() - dob.getFullYear();
      const monthDifference = today.getMonth() - dob.getMonth();
      const dayDifference = today.getDate() - dob.getDate();
    
      // Adjust age if the current date is before the birth date in the same year
      if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
        age--;
      }
    
      if (age < 14) {
        errors.dob = 'Age must be above 14 years.';
        isValid = false;
      }
    }

    if (!emailRegex.test(internData.email)) {
      setMessage("Please enter a valid email address.");
      isValid = false;
    }

    // if (!internData.mobile) {
    //   errors.mobile = 'Mobile Number is required.';
    //   isValid = false;
    // } else if (!mobileRegex.test(internData.mobile)) {
    //   errors.mobile = 'Mobile Number must be exactly 10 digits.';
    //   isValid = false;
    // }

    if (internData.mobile && internData.mobile.trim() !== '') {
      if (!mobileRegex.test(internData.mobile)) {
        errors.mobile = 'Mobile Number must be exactly 10 digits and start with a digit other than 0.';
        isValid = false;
      }
    }
    if (!internData.district) {
      errors.district = 'District is required.';
      isValid = false;
    }
    if (!internData.state) {
      errors.state = 'State is required.';
      isValid = false;
    }
    if (!internData.city) {
      errors.city = 'City is required.';
      isValid = false;
    }
    if (!internData.country) {
      errors.country = 'Country is required.';
      isValid = false;
    }
    if (!internData.address) {
      errors.address = 'Address is required.';
      isValid = false;
    }
    if (!internData.pincode) {
      errors.pincode = 'Pincode is required.';
      isValid = false;
    }
    if (!internData.education) {
      errors.education = 'Education is required.';
      isValid = false;
    }
    if (!internData.institution) {
      errors.institution = 'Institution is required.';
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  const handleCertificateDownload = () => {
    // console.log("Internship Certificate Download Triggered");
  };

  const [viewCertificate, setViewCertificate] = useState('false');
  const handleCertificateView = () => {
    setViewCertificate(!viewCertificate);
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const [showTnC, setShowTnC] = useState(false);

const toggleTnC = () => {
  setShowTnC((prev) => !prev);
};

  return (
    <MainLayout>
      <div className='text-lg mt-12 mb-4 mx-8 border-b border-b-gray-300 text-white'>
        <h2>Intern Profile</h2>
      </div>
      <div className='main-content mt-4 border border-b-gray-300 p-6 rounded-lg bg-gray-800 text-white'>
        {!internExists && (
          <div className='flex justify-center mt-4'>
            <button
              className='create-intern-button px-4 py-2 bg-blue-600 rounded-full text-white hover:bg-blue-700'
              onClick={handleCreateIntern}
              disabled={creatingIntern}
            >
              Apply for Think5Whys Internship
            </button>
            <InternTnC/>
          </div>
        )}
        {internExists && (
          <>
            <div className='flex flex-col gap-4'>
              <button
                onClick={toggleTnC}
                className="bg-blue-500 text-white px-4 py-2 rounded shadow-md hover:bg-blue-600 transition"
              >
                Think5Whys Internship Terms & Conditions
              </button>

              {/* Conditionally render the InternTnC component */}
              {showTnC && (
                <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                  <InternTnC />
                </div>
              )}
              <div className='flex flex-col text-yellow-200'>
                  <p>Name: {internData.name}</p>
                  <p>Think5Whys InternId: {internData.think5whysCode}</p>
                  <p>Email Id: {internData.email}</p>
                  <p>Internship Status:{' '} 
                    {internData.internStatus === 'Completed' ? 
                   "Intership Completed" : 
                   `${internData.internStatus}`}
                  </p>
                  <p>
                  Internship Period:{' '} 
                  {internData.dateStarts ? 
                    `${formatDateTime(internData.dateStarts)} to ${internData.dateEnds ? formatDateTime(internData.dateEnds) : 'Not Ended Yet'}` : 
                    'Not Started Yet'}
                  </p>
                  <p>Total Stipend Earned: {internData.stipend}</p>
                  <p className='text-white'>Remarks: {internData.remarks}</p>
                  <button
                    className={`px-4 py-2 rounded focus:outline-none focus:ring-2 transition ${
                      internData.internStatus === 'Completed'
                        ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-300'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                    onClick={handleCertificateView}
                    title={
                      internData.internStatus === 'Completed'
                        ? 'Download Internship Certificate'
                        : 'Internship must be completed to download the certificate'
                    }
                    disabled={internData.internStatus !== 'Completed'}
                  >
                    View Certificate Details
                  </button>
                  <button
                    className={`px-4 py-2 rounded focus:outline-none focus:ring-2 transition ${
                      internData.internStatus === 'Completed'
                        ? 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-300'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                    onClick={handleCertificateDownload}
                    title={
                      internData.internStatus === 'Completed'
                        ? 'Download Internship Certificate'
                        : 'Internship must be completed to download the certificate'
                    }
                    disabled={internData.internStatus !== 'Completed'}
                  >
                    Download Internship Certificate
                  </button>

              </div>
              {/* {viewCertificate  ? "yes" : "no"} */}
              {viewCertificate &&
                <div className="flex flex-col">
                  <p>{internData.certificateText}</p>
                </div>
              }

              <div className='flex flex-col'>
                  <p className='text-yellow-200'>Mentor Details:</p>
                  <p>Name: {internData.mentorName}</p>
                  <p>Email Id: {internData.mentorEmail}</p>
                  <p>Contact# {internData.mentorContact}</p>
                  <p>Details: {internData.mentorDetails}</p>
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Date of Birth:</label>
                <input
                  className="intern-date bg-gray-600 rounded-md px-2 py-1"
                  type="date"
                  style={{width: "150px"}}
                  value={internData.dob ? internData.dob.substring(0, 10) : ''}
                  onChange={(e) => setInternData({ ...internData, dob: e.target.value })}
                />
                <p className='text-yellow-200'>The legal minimum age for internships in India is 14 years as per the Child Labour (Prohibition and Regulation) Act, 1986, 
                  provided the work is not hazardous and have educational value.</p>
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Intern Gender:</label>
                <select
                  className="intern-select bg-gray-600 rounded-md px-2 py-1"
                  style={{width: "100px"}}
                  value={internData.gender}
                  onChange={(e) => setInternData({ ...internData, gender: e.target.value })}
                >
                  <option value={0}>Male</option>
                  <option value={1}>Female</option>
                  <option value={2}>Other</option>
                </select>
                {errors.gender && <span className="text-red-500 text-xs">{errors.gender}</span>}
              </div>

              <div className='flex flex-col'>
                <label className='text-yellow-200'>Mobile Number:</label>
                <input
                  className="intern-input bg-gray-600 rounded-md px-2 py-1"
                  type="tel"
                  value={internData.mobile}
                  onChange={(e) => setInternData({ ...internData, mobile: e.target.value })}
                />
                {errors.mobile && <span className="text-red-500 text-xs">{errors.mobile}</span>}
              </div>

              <div className='flex flex-col'>
                  <label className='text-yellow-200'>District:</label>
                  <input 
                      className="intern-input bg-gray-600 rounded-md px-2 py-1"
                      style={{ border: '1px solid #ccc'}}
                      type="text"
                      value={internData.district}
                      onChange={(e) => setInternData({ ...internData, district: e.target.value })}
                  />
              </div>

              <div className='flex flex-col'>
                  <label className='text-yellow-200'>City:</label>
                  <input 
                      className="intern-input bg-gray-600 rounded-md px-2 py-1"
                      style={{ border: '1px solid #ccc'}}
                      type="text"
                      value={internData.city}
                      onChange={(e) => setInternData({ ...internData, city: e.target.value })}
                  />
              </div>

              <div className='flex flex-col'>
                  <label>State:</label>
                  <input
                      className="intern-input bg-gray-600 rounded-md px-2 py-1"
                      type="text"
                      style={{ border: '1px solid #ccc' }}
                      value={internData.state}
                      onChange={(e) => setInternData({ ...internData, state: e.target.value })}
                  />
              </div>

              <div className='flex flex-col'>
                  <label>Country:</label>
                  <input
                      className="intern-input bg-gray-600 rounded-md px-2 py-1"
                      type="text"
                      style={{ border: '1px solid #ccc' }}
                      value={internData.country}
                      onChange={(e) => setInternData({ ...internData, country: e.target.value })}
                  />
              </div>

              <div className='flex flex-col'>
                  <label>Postal Code:</label>
                  <input
                      className="intern-input bg-gray-600 rounded-md px-2 py-1"
                      type="text"
                      style={{
                          border: '1px solid #ccc',
                          width: '100px',
                      }}
                      value={internData.pincode}
                      onChange={(e) => setInternData({ ...internData, pincode: e.target.value })}
                  />
              </div>

              <div className='flex flex-col'>
                  <label>Address:</label>
                  <textarea
                      className="bg-gray-600 rounded-md px-2 py-1"
                      style={{
                          border: '1px solid #ccc',
                          maxWidth: '650px',
                          maxHeight: '150px',
                      }}
                      value={internData.address}
                      onChange={(e) => setInternData({ ...internData, address: e.target.value })}
                  />
              </div>

              <div className='flex flex-col'>
                  <label className='text-yellow-200'>Education:</label>
                  <input
                      className="intern-input bg-gray-600 rounded-md px-2 py-1"
                      type="text"
                      value={internData.education}
                      onChange={(e) => setInternData({ ...internData, education: e.target.value })}
                  />
              </div>

              <div className='flex flex-col'>
                  <label className='text-yellow-200'>Institution:</label>
                  <input
                      className="intern-input bg-gray-600 rounded-md px-2 py-1"
                      type="text"
                      value={internData.institution}
                      onChange={(e) => setInternData({ ...internData, institution: e.target.value })}
                  />
              </div>

              {/* <div className='flex flex-col'>
                  <label>Social Links:</label>
                  <textarea
                      className="bg-gray-600 rounded-md px-2 py-1"
                      style={{
                          border: '1px solid #ccc',
                          maxWidth: '650px',
                          maxHeight: '100px',
                      }}
                      value={internData.socialLinks}
                      onChange={(e) => setInternData({ ...internData, socialLinks: e.target.value })}
                  />
              </div> */}

              <div className='flex flex-col'>
                  <label>Write About Yourself in Brief:</label>
                  <textarea
                      className="bg-gray-600 rounded-md px-2 py-1"
                      style={{
                          border: '1px solid #ccc',
                          maxWidth: '650px',
                          maxHeight: '150px',
                      }}
                      value={internData.bio}
                      onChange={(e) => setInternData({ ...internData, bio: e.target.value })}
                  />
              </div>
            </div>
            {updateSuccess && (
              <div className="success-message text-green-500 text-lg mt-4">
                {updateSuccessMsg}
              </div>
            )}
            <div className='flex mt-6'>
              <button
                className='update-intern-button px-4 py-2 bg-green-600 rounded-full text-white hover:bg-green-700 w-40'
                onClick={handleInternUpdate}
              >
                Update
              </button>
              <button
                className='update-intern-button px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 w-40'
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </>
        )}

        {!internExists && createSuccess && (
          <div className="flex flex-col justify-center success-message text-green-500 text-xl mt-4">
            {createSuccessMsg}
            <button
                className='update-intern-button px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 w-40'
                onClick={() => navigate("/internsplanet-intern")}
              >
                OK
            </button>
          </div>
        )}
     </div>
    </MainLayout>
  );
};

export default Intern;



